import { isObject } from "util";
import { DtoHelper, IDiffOptions } from "./dto-helper";
import { IdMap } from "../../interfaces/id-map.interface";

// do not remove this import or --prod compilation will fail
import * as RefDef from "reflect-metadata";

import { isDefined, isString } from "ionic-angular/util/util";
import { isInArray } from "../../util/is-in-array";

const DTO_LISTS = {
    INCLUDE: "dto:include",
    JSON: "dto:json",
    REF_LIST: "dto:refList",
    REF: "dto:ref",
    NUMBER: "dto:number"
};

const addToList = (list: string, target: Function, name: string) => {
    let value = Reflect.getMetadata(list, target);
    value = !value ? [name] : [...value, name];
    Reflect.defineMetadata(list, value, target);
};

export function DtoInclude(proto, name): void {
    addToList(DTO_LISTS.INCLUDE, proto, name);
}

export function DtoIncludeJson(proto, name): void {
    addToList(DTO_LISTS.JSON, proto, name);
}

export function DtoRef(proto, name): void {
    addToList(DTO_LISTS.REF, proto, name);
}

export function DtoRefList(proto, name): void {
    addToList(DTO_LISTS.REF_LIST, proto, name);
}

export function DtoIncludeNumber(proto, name): void {
    addToList(DTO_LISTS.NUMBER, proto, name);
}

export class Dto {

    constructor(data?: any) {
        if (!data) return;

        Object.keys(data).forEach(key => {
            this[key] = data[key];
        });
    }

    toObject(): any {
        const dto = {};

        const props = [
            ...this.getDtoProperties(),
            ...this.getDtoRefProperties(),
            ...this.getDtoRefListProperties(),
            ...this.getDtoJsonProperties(),
            ...this.getDtoNumbers()
        ];

        Object.keys(this).forEach(key => {
            if (isInArray(key, props)) {
                dto[key] = this[key];
            }
        });
        return dto;
    }

    /**
     * Returns only the properties of the object that should be sent to the Rest API in the correct format, e.g.
     * JSON properties are stringified
     * @returns {{}}
     */
    getRequestBody(): any {

        const body = {};

        const dtoProps = this.getDtoProperties();
        const numberProps = this.getDtoNumbers();
        const jsonProps = this.getDtoJsonProperties();
        const refProps = this.getDtoRefProperties();
        const refListProps = this.getDtoRefListProperties();

        const props = [
            ...dtoProps,
            ...numberProps,
            ...jsonProps,
            ...refProps,
            ...refListProps
        ];

        Object.keys(this).forEach(key => {
            if (isInArray(key, props)) {
                body[key] = this[key];
            }
        });

        Object.keys(this).forEach(key => {
            if (isInArray(key, jsonProps)) {
                const value = this[key];
                body[key] = isObject(value) ? JSON.stringify(value) : value;
            }
        });

        Object.keys(this).forEach(key => {
            if (isInArray(key, numberProps)) {
                const value = this[key];
                body[key] = value ? this.parseDtoNumberValue(value) : value;
            }
        });

        Object.keys(body).forEach(key => {
            if (body[key] === null || body[key] === undefined) {

                if (isInArray(key, refListProps)) {
                    body[key] = [];
                } else {
                    body[key] = "~";
                }
            }
        });

        return body;
    }

    protected parseDtoNumberValue(value: any): any {

        switch (true) {
            case value.toString().trim() === "~":
            case value.toString().trim() === "":
            case isNaN(parseInt(value, 10)) && isNaN(parseFloat(value)): {
                return "~";
            }
            default: {

                if (value.toString().match(/\./)) {
                    return parseFloat(value);
                } else {
                    return parseInt(value, 10);
                }
            }
        }
    }

    /**
     * Returns a new DTO instance with values of data,
     * - sets ref list properties correctly e.g. ["foo"] becomes ["+foo"]
     * @param data
     */
    initialise(data: any): Dto {

        const newData = {};

        Object.keys(data).forEach(key => {
            newData[key] = data[key];
        });

        this.getDtoRefListProperties().forEach(key => {
            newData[key] = (data[key] || []).map(value => `+${value}`);
        });

        return new (this.constructor as any)(newData);
    }

    /**
     * Returns a new Dto instance containing only the properties that changed
     * @param data
     */
    apply(data: any): Dto {

        const diff = this.diff(data, {
            refListProperties: this.getDtoRefListProperties(),
            ignoreProperties: []
        });

        return new (this.constructor as any)(diff);

    }

    /**
     * Maps any properties of this object that are ids using the provided idMap
     * @param idMap
     * @deprecated DO NOT USE
     */
    mapIds(idMap: IdMap): void {

        // base properties
        ["id", "createdBy", "modifiedBy"].forEach(prop => {
            if (isDefined(this[prop])) {
                this[prop] = this.mapId(this[prop], idMap);
            }
        });

        this.getDtoRefProperties().forEach(prop => {
            if (isDefined(this[prop])) {
                this[prop] = this.mapId(this[prop], idMap);
            }
        });

        this.getDtoRefListProperties().forEach(prop => {
            if (isDefined(this[prop])) {
                this[prop] = this.mapRefListIds(this[prop], idMap);
            }
        });
    }

    /**
     *
     * @param {Array<string>} list
     * @param {IdMap} idMap
     * @return {Array<string>}
     * @deprecated DO NOT USE
     */
    protected mapRefListIds(list: Array<string>, idMap: IdMap): Array<string> {
        return (list || []).map(id => {
            if (id.startsWith("+")) {
                return `+${this.mapId(id.substring(1), idMap)}`;
            } else if (id.startsWith("-")) {
                return `-${this.mapId(id.substring(1), idMap)}`;
            } else {
                return this.mapId(id, idMap);
            }
        });
    }

    /**
     *
     * @param {string} from
     * @param {IdMap} idMap
     * @return {string}
     * @deprecated DO NOT USE
     */
    protected mapId(from: string, idMap: IdMap): string {

        if (!isDefined(idMap[from])) {
            return from;
        }

        return idMap[from];
    }

    diff(data, options?: IDiffOptions): any {
        return DtoHelper.diff(this, data, options);
    }

    private getDtoProperties(): Array<string> {
        return this.getPropertiesByDecorator(DTO_LISTS.INCLUDE);
    }

    private getDtoJsonProperties(): Array<string> {
        return this.getPropertiesByDecorator(DTO_LISTS.JSON);
    }

    private getDtoRefListProperties(): Array<string> {
        return this.getPropertiesByDecorator(DTO_LISTS.REF_LIST);
    }

    private getDtoRefProperties(): Array<string> {
        return this.getPropertiesByDecorator(DTO_LISTS.REF);
    }

    private getDtoNumbers(): Array<string> {
        return this.getPropertiesByDecorator(DTO_LISTS.NUMBER);
    }

    private getPropertiesByDecorator(decorator): Array<string> {
        return Reflect.getMetadata(decorator, this) || [];
    }
}



// WEBPACK FOOTER //
// ./src/core/store/dto/dto.ts